/**
 * PermissionContext
 * =================
 * Loads the current user's effective permissions from /api/auth/me/permissions
 * and exposes helpers to gate UI elements.
 *
 * Safety rules:
 *  - SYSTEM_DEVELOPER and is_superuser ALWAYS bypass (cannot be locked out).
 *  - While loading, all checks return true (UI does NOT flicker / hide things).
 *  - Unknown modules / actions default to true (additive backend changes don't
 *    accidentally hide existing UI).
 *  - The hook is null-safe outside the provider (returns allow-all). This means
 *    pages that render before the provider mounts (e.g. login) never crash.
 */
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

const ALLOW_ALL = {
  loading: false,
  bypass: true,
  effective: {},
  modules: {},
  role: null,
  customPermissions: {},
  can: () => true,
  canAny: () => true,
  canAll: () => true,
  canModule: () => true,
  refresh: async () => {},
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [effective, setEffective] = useState({});
  const [customPermissions, setCustomPermissions] = useState({});
  const [modules, setModules] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Demo users get full access (demo tenants are disposable 24h sandboxes).
  // Cover BOTH the reserved demo email domain AND any user whose tenant is
  // flagged is_demo (e.g. a nurse created inside a demo tenant for testing),
  // surfaced by the backend as user.is_demo_tenant.
  const isDemoEmail = !!(user && user.email && user.email.toLowerCase().endsWith('@demo.ospito.local'));
  const isDemoUser = isDemoEmail || !!(user && user.is_demo_tenant);
  // Superusers bypass role-based permission checks — but NOT when they are
  // simulating a different plan (in that case they should experience the app
  // as a regular ADMIN user of the simulated plan).
  // We detect simulation via a session-storage flag set by the Header
  // simulator before the page reload.
  const isSimulatingPlan = !!sessionStorage.getItem('ospito_simulating_plan');
  const isBypass = !isSimulatingPlan && !!(user && (user.role === 'SYSTEM_DEVELOPER' || user.is_superuser || isDemoUser));

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setEffective({});
      setCustomPermissions({});
      setRole(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [meRes, modsRes] = await Promise.all([
        authAPI.getMyPermissions(),
        authAPI.getPermissionModules(),
      ]);
      setEffective(meRes.data?.effective_permissions || {});
      setCustomPermissions(meRes.data?.custom_permissions || {});
      setRole(meRes.data?.role || null);
      setModules(modsRes.data?.modules || {});
      setError(false);
    } catch (err) {
      // Fail-closed: deny permissions on error to prevent unintended access
      // eslint-disable-next-line no-console
      console.error('Failed to load permissions:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const can = useCallback((moduleKey, action = 'view') => {
    if (isBypass) return true;
    if (loading) return false;
    if (error) return false;
    if (!moduleKey) return true;
    const mod = effective[moduleKey];
    if (!mod) {
      // Unknown module to backend → don't hide UI (additive safety)
      // BUT if backend DOES know the module and explicitly denies, we'll catch it below
      if (modules[moduleKey] === undefined) return true;
      return false;
    }
    // If the action key is unknown to the backend module spec, allow (additive safety)
    if (modules[moduleKey] && Array.isArray(modules[moduleKey].actions)
        && !modules[moduleKey].actions.includes(action)) {
      return true;
    }
    return !!mod[action];
  }, [effective, modules, loading, isBypass, error]);

  const canAny = useCallback((pairs) => {
    if (isBypass) return true;
    if (!Array.isArray(pairs) || pairs.length === 0) return true;
    return pairs.some(p => can(p.module, p.action || 'view'));
  }, [can, isBypass]);

  const canAll = useCallback((pairs) => {
    if (isBypass) return true;
    if (!Array.isArray(pairs) || pairs.length === 0) return true;
    return pairs.every(p => can(p.module, p.action || 'view'));
  }, [can, isBypass]);

  /** True if the user has ANY action enabled in this module. */
  const canModule = useCallback((moduleKey) => {
    if (isBypass) return true;
    if (loading) return true;
    if (!moduleKey) return true;
    const mod = effective[moduleKey];
    if (!mod) return modules[moduleKey] === undefined; // unknown → allow
    return Object.values(mod).some(Boolean);
  }, [effective, modules, loading, isBypass]);

  const value = useMemo(() => ({
    loading,
    bypass: isBypass,
    effective,
    modules,
    role,
    customPermissions,
    can,
    canAny,
    canAll,
    canModule,
    refresh: fetchPermissions,
  }), [loading, isBypass, effective, modules, role, customPermissions, can, canAny, canAll, canModule, fetchPermissions]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Primary hook. Always safe to call — falls back to allow-all if no provider.
 */
export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  return ctx || ALLOW_ALL;
};

/**
 * Convenience hook for a single permission check.
 * Usage: const canEdit = useCan('patients', 'edit');
 */
export const useCan = (moduleKey, action = 'view') => {
  const { can } = usePermissions();
  return can(moduleKey, action);
};
